import { customSeriesDefaultOptions } from 'lightweight-charts'

/**
 * ZoneFillSeries — Relleno plano de zonas (caja del rango / riesgo-beneficio).
 *
 * Copia exacta de `patron_ruptura_quantum/ZoneFillSeries.js` (molde
 * CloudFillSeries de supertrend_quantum / BoxFillSeries de ict_quantum,
 * Standard A.3). Cada indicador mantiene su propia copia -- estructura de
 * carpeta autocontenida (Standard C.1), sin imports cruzados entre carpetas
 * de indicador.
 */
class ZoneFillPaneRenderer {
  constructor() {
    this._data = null
    this._options = null
  }

  update(data, options) {
    this._data = data
    this._options = options
  }

  draw(target, priceConverter) {
    if (!this._data || !this._options) return
    const { bars, visibleRange } = this._data
    if (!visibleRange || bars.length < 2) return

    const from = Math.max(0, Math.floor(visibleRange.from))
    const to = Math.min(bars.length - 1, Math.ceil(visibleRange.to))
    if (from >= to) return

    target.useMediaCoordinateSpace(scope => {
      const ctx = scope.context

      let segStart = from
      while (segStart <= to) {
        const segColor = bars[segStart]?.originalData?.color ?? this._options.fillColor
        let segEnd = segStart
        while (segEnd + 1 <= to && (bars[segEnd + 1]?.originalData?.color ?? this._options.fillColor) === segColor) {
          segEnd++
        }
        const drawFrom = segStart > from ? segStart - 1 : segStart
        const drawTo = segEnd < to ? segEnd + 1 : segEnd

        const yPairOf = (k) => {
          const d = bars[k]?.originalData
          if (!d || !Number.isFinite(d.upper) || !Number.isFinite(d.lower)) return null
          const yUpper = priceConverter(d.upper)
          const yLower = priceConverter(d.lower)
          if (yUpper == null || yLower == null) return null
          return { yUpper, yLower }
        }

        const points = []
        for (let k = drawFrom; k <= drawTo; k++) {
          const pair = yPairOf(k)
          const x = bars[k]?.x
          if (!pair || !Number.isFinite(x)) continue
          points.push({ x, y: pair.yUpper })
        }
        for (let k = drawTo; k >= drawFrom; k--) {
          const pair = yPairOf(k)
          const x = bars[k]?.x
          if (!pair || !Number.isFinite(x)) continue
          points.push({ x, y: pair.yLower })
        }

        if (points.length > 2) {
          ctx.beginPath()
          ctx.moveTo(points[0].x, points[0].y)
          for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y)
          ctx.closePath()
          ctx.fillStyle = segColor
          ctx.fill()
        }

        segStart = segEnd + 1
      }
    })
  }
}

export class ZoneFillSeries {
  constructor() {
    this._renderer = new ZoneFillPaneRenderer()
  }

  renderer() {
    return this._renderer
  }

  update(data, seriesOptions) {
    this._renderer.update(data, seriesOptions)
  }

  priceValueBuilder(plotRow) {
    return [plotRow.upper, plotRow.lower]
  }

  isWhitespace(data) {
    return data == null || data.upper == null || data.lower == null
  }

  defaultOptions() {
    return {
      ...customSeriesDefaultOptions,
      color: 'rgba(0,0,0,0)',
      fillColor: 'rgba(100,100,139,0.07)',
    }
  }
}
